import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GraduationCap, Download, ExternalLink, Calendar, MapPin } from 'lucide-react';

const Education = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const education = [
    {
      degree: 'BSc (Hons) in Software Engineering',
      institution: 'University of Bedfordshire',
      period: '2024–2027',
      location: 'United Kingdom',
      description: 'Currently pursuing a comprehensive software engineering degree focusing on modern development practices, AI and full-stack development.',
      achievements: ['Full-Stack Development', 'AI', 'Software Architecture', 'Database Design', "Networking & CyberSecurity", "Devops"],
      current: true
    },
    {
      degree: '+2 Science',
      institution: 'Glacier International College',
      period: '2022–2023',
      location: 'Nepal',
      description: 'Completed higher secondary education with focus on science and mathematics, laying the foundation for technical studies.',
      achievements: ['Mathematics Excellence', 'Science Projects', 'Leadership Skills'],
      current: false
    }
  ];

  return (
    <section id="education" className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title gradient-text">Education</h2>
          <p className="section-subtitle">My academic journey</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {education.map((edu, index) => (
            <motion.div
              key={edu.institution}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="glass-effect p-8 rounded-2xl relative"
            >
              {edu.current && (
                <div className="absolute top-4 right-4 bg-primary-gold text-primary-bg px-3 py-1 rounded-full text-sm font-semibold z-10">
                  Current
                </div>
              )}
              
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-gold to-primary-violet rounded-full flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="text-primary-bg" size={24} />
                </div>
                <div className="flex-1 pr-16">
                  <h3 className="text-2xl font-bold text-primary-gold font-playfair mb-2">
                    {edu.degree}
                  </h3>
                  <p className="text-xl text-primary-text font-semibold mb-2">
                    {edu.institution}
                  </p>
                  <div className="flex items-center gap-4 text-primary-muted text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>{edu.period}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin size={16} />
                      <span>{edu.location}</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-primary-text mb-6 leading-relaxed">
                {edu.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {edu.achievements.map((achievement) => (
                  <span
                    key={achievement}
                    className="px-3 py-1 bg-primary-gold/10 text-primary-gold text-sm rounded-full border border-primary-gold/20"
                  >
                    {achievement}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Resume Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="glass-effect p-8 rounded-2xl text-center"
        >
          <h3 className="text-2xl font-bold text-primary-gold mb-4 font-playfair">
            Download My Resume
          </h3>
          <p className="text-primary-muted mb-6 max-w-2xl mx-auto">
            Get a detailed overview of my skills, experience, and projects in a comprehensive resume format.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary flex items-center gap-2"
              onClick={() => window.open('/resume.pdf', '_blank')}
            >
              <ExternalLink size={20} />
              View Full Resume
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary flex items-center gap-2"
              onClick={() => {
                const link = document.createElement('a');
                link.href = '/resume.pdf';
                link.download = 'Dev_Bhushan_Bhatta_Resume.pdf';
                link.click();
              }}
            >
              <Download size={20} />
              Download PDF
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Education; 